import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "./CurrencyMasterPage";
import { Field } from "./BankAccountsPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Save } from "lucide-react";

const PAY_FIELDS = [
    ["beneficiary_name", "Beneficiary Name"],
    ["beneficiary_address", "Beneficiary Address"],
    ["bank_name", "Bank Name"],
    ["bank_address", "Bank Address"],
    ["account_number", "Account Number"],
    ["ach_routing", "ACH Routing"],
    ["fedwire_routing", "Fedwire Routing"],
    ["swift_code", "SWIFT / BIC"],
    ["reference", "Reference"],
];

const CURRENCIES = ["USD", "EUR", "AED", "KWD", "INR", "USDT"];

export default function EntityInvoiceSettingsPage() {
    const { user } = useAuth();
    if (user?.role !== "admin") {
        return <div className="text-slate-500 text-sm">Admin only.</div>;
    }
    return <Inner />;
}

function Inner() {
    const [entities, setEntities] = useState([]);
    const [activeId, setActiveId] = useState("");
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const { data } = await api.get("/entities");
        setEntities(data);
        if (data.length && !activeId) {
            setActiveId(data[0].id);
            setForm(buildForm(data[0]));
        }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, []);

    function buildForm(entity) {
        const s = entity.invoice_settings || {};
        return {
            address_text: (s.address_lines || []).join("\n"),
            phone: s.phone || "",
            website: s.website || "",
            tagline: s.tagline || "",
            email_cc_text: (s.email_cc || []).join(", "),
            payment_instructions: s.payment_instructions || {},
        };
    }

    const switchEntity = (id) => {
        const e = entities.find((x) => x.id === id);
        if (!e) return;
        setActiveId(id);
        setForm(buildForm(e));
    };

    const save = async () => {
        if (!form) return;
        setBusy(true);
        try {
            const payload = {
                address_lines: form.address_text.split("\n").map((l) => l.trim()).filter(Boolean),
                phone: form.phone,
                website: form.website,
                tagline: form.tagline,
                email_cc: (form.email_cc_text || "")
                    .replace(/;/g, ",")
                    .split(/[,\n]/)
                    .map((s) => s.trim())
                    .filter(Boolean),
                payment_instructions: form.payment_instructions,
            };
            await api.put(`/entities/${activeId}/invoice-settings`, payload);
            toast.success("Invoice settings saved");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    const updatePay = (currency, key, value) => {
        const cur = form.payment_instructions[currency] || {};
        setForm({
            ...form,
            payment_instructions: {
                ...form.payment_instructions,
                [currency]: { ...cur, [key]: value },
            },
        });
    };

    const activeEntity = entities.find((e) => e.id === activeId);

    return (
        <div data-testid="entity-invoice-settings">
            <PageHeader
                title="Entity Invoice Settings"
                eyebrow="Administration"
                description="Letterhead and payment instructions used when generating PDF invoices for each entity."
            />

            <div className="grid lg:grid-cols-4 gap-3">
                <div className="bg-white border border-slate-200 p-3 lg:col-span-1">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 px-2 py-1.5">
                        Entities
                    </div>
                    {entities.map((e) => (
                        <button
                            key={e.id}
                            onClick={() => switchEntity(e.id)}
                            data-testid={`eis-entity-${e.code}`}
                            className={`w-full text-left px-2 py-2 text-[13px] border-l-2 ${
                                activeId === e.id
                                    ? "border-l-[#002FA7] bg-blue-50/40 font-medium"
                                    : "border-l-transparent hover:bg-slate-50"
                            }`}
                        >
                            <div>{e.name}</div>
                            <div className="font-mono text-[10.5px] text-slate-500">
                                {e.code} · {e.country}
                            </div>
                        </button>
                    ))}
                </div>

                <div className="lg:col-span-3 space-y-4">
                    {form && activeEntity && (
                        <>
                            <div className="bg-white border border-slate-200 p-5">
                                <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-3">
                                    Letterhead — {activeEntity.name}
                                </div>
                                <div className="grid md:grid-cols-2 gap-3">
                                    <div className="md:col-span-2">
                                        <Field label="Address (one line per row)">
                                            <Textarea
                                                rows={4}
                                                value={form.address_text}
                                                onChange={(e) => setForm({ ...form, address_text: e.target.value })}
                                                className="rounded-sm font-mono text-[13px]"
                                                data-testid="eis-address-input"
                                            />
                                        </Field>
                                    </div>
                                    <Field label="Phone">
                                        <Input
                                            value={form.phone}
                                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                            className="rounded-sm h-9 font-mono"
                                            data-testid="eis-phone-input"
                                        />
                                    </Field>
                                    <Field label="Website">
                                        <Input
                                            value={form.website}
                                            onChange={(e) => setForm({ ...form, website: e.target.value })}
                                            className="rounded-sm h-9 font-mono"
                                            data-testid="eis-website-input"
                                        />
                                    </Field>
                                    <div className="md:col-span-2">
                                        <Field label="Tagline / Footer">
                                            <Input
                                                value={form.tagline}
                                                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                                                className="rounded-sm h-9"
                                                data-testid="eis-tagline-input"
                                            />
                                        </Field>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-slate-200 p-5">
                                <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">
                                    Internal email CC — {activeEntity.name}
                                </div>
                                <div className="text-[11.5px] text-slate-500 mb-3">
                                    These email IDs are automatically added as <b>CC</b> whenever a sales invoice is emailed to a customer from this entity. Comma or newline separated.
                                </div>
                                <Textarea
                                    rows={2}
                                    value={form.email_cc_text}
                                    onChange={(e) => setForm({ ...form, email_cc_text: e.target.value })}
                                    placeholder="accounts@yourcompany.com, finance@yourcompany.com"
                                    className="rounded-sm font-mono text-[12.5px]"
                                    data-testid="eis-email-cc-input"
                                />
                            </div>

                            <div className="bg-white border border-slate-200 p-5">
                                <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-3">
                                    Payment instructions (per currency)
                                </div>
                                <Tabs defaultValue="USD" className="w-full">
                                    <TabsList className="rounded-sm bg-slate-100">
                                        {CURRENCIES.map((c) => (
                                            <TabsTrigger key={c} value={c} className="rounded-sm font-mono">
                                                {c}
                                            </TabsTrigger>
                                        ))}
                                    </TabsList>
                                    {CURRENCIES.map((c) => (
                                        <TabsContent key={c} value={c} className="mt-3">
                                            <div className="grid md:grid-cols-2 gap-3">
                                                {PAY_FIELDS.map(([key, label]) => (
                                                    <Field key={key} label={label}>
                                                        <Input
                                                            value={(form.payment_instructions[c] || {})[key] || ""}
                                                            onChange={(e) => updatePay(c, key, e.target.value)}
                                                            className="rounded-sm h-9 font-mono text-[12px]"
                                                            data-testid={`eis-pay-${c}-${key}`}
                                                        />
                                                    </Field>
                                                ))}
                                            </div>
                                        </TabsContent>
                                    ))}
                                </Tabs>
                            </div>

                            <div className="flex justify-end">
                                <Button
                                    onClick={save}
                                    disabled={busy}
                                    className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f]"
                                    data-testid="eis-save-btn"
                                >
                                    <Save size={14} className="mr-1.5" />
                                    {busy ? "Saving…" : "Save invoice settings"}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
